import {InputLabel, styled, SxProps, TextField, TextFieldProps} from "@mui/material";
import theme, {FONTS} from "../../../constants/theme";
import {scale} from "../../../utils";
import React, {useState} from "react";

interface Props {
  htmlFor: string;
  placeholder: string;
  label: string;
  errorLabel?: string;
  validate?: (value: string) => boolean;
  setValue: (value: string) => void;
  setErrorValue: (value: boolean) => void;
  type?: string;
  variant?: 'filled' | 'standard';
  inputSx?: SxProps;
  wrapperSx?: SxProps;
  startAdornment?: React.ReactNode;
  props?: TextFieldProps;
  disabled?: boolean;
  defaultValue?: string;
  value?: string;
  required?: boolean;
}

const InputWrapper = styled('div')({
  width: scale(270.2),
  height: scale(53.14),
  display: 'flex',
  rowGap: 2,
  flexDirection: 'column',
  flexBasis: '50%',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: {
    width: scale(410),
    height: scale(62),
  },
});

const inputLabelStyle = {
  color: theme.palette.ceramic.contrastText,
  fontFamily: FONTS.MONTSERRAT,
  fontSize: scale(16),
  lineHeight: scale(20),
  fontWeight: 700,
  textTransform: 'uppercase',
  [theme.breakpoints.down('sm')]: {
    fontSize: scale(18),
  },
}

const slotProps = {
  input: {
    sx: {
      fontFamily: FONTS.MONTSERRAT,
      fontWeight: 300,
      fontSize: scale(16),
      color: 'black',
      height: scale(40),
      width: scale(299.8),
      boxSizing: 'border-box',
      borderRadius: 0,
      '& input': {
        paddingTop: scale(7),
        paddingLeft: scale(8),
        paddingBottom: scale(6),
      },
      [theme.breakpoints.down('sm')]: {
        width: scale(410),
        height: scale(45),
        paddingTop: scale(10),
        paddingBottom: scale(10),
        fontSize: scale(18),
      },
    },
  },
}

const inputRootProps = {
  height: scale(32),
  textWrap: 'nowrap',
  '& .MuiFormHelperText-root': {
    fontSize: scale(16),
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiFormHelperText-root': {
      fontSize: scale(18),
    },
  },
}

const ThemedTextInput = ({htmlFor, placeholder, label, errorLabel, validate, setValue, type, setErrorValue, variant, inputSx, wrapperSx, startAdornment, props, disabled = false, defaultValue, value, required = true}: Props) => {
  const [error, setError] = useState(false);
  const [eLabel, setELabel] = useState<string>('');
  const mergedSlotProps = {
    ...slotProps,
    ...(inputSx || {}),
    input: {
      ...slotProps.input,
      ...((inputSx as any)?.input || {}),
      ...(startAdornment ? {startAdornment} : {}),
    },
  };
  return (
    <InputWrapper sx={wrapperSx}>
      <InputLabel
        htmlFor={htmlFor}
        sx={[inputLabelStyle, {color: error ? theme.palette.error.main : theme.palette.ceramic.contrastText}]}
      >
        {label}
      </InputLabel>
      <TextField
        id={htmlFor}
        placeholder={placeholder}
        variant={variant ?? 'filled'}
        color={'primary'}
        size={'small'}
        slotProps={mergedSlotProps}
        error={error}
        sx={inputRootProps}
        helperText={eLabel}
        disabled={disabled}
        defaultValue={defaultValue ?? undefined}
        value={value ?? undefined}
        onChange={(event) => {
          if (type === 'CF') {
            event.target.value = event.target.value.toUpperCase();
          }
          setValue(event.target.value)
        }}
        onBlur={(event) => {
          if (required) {
            const isValid = validate
              ? validate(event.target.value)
              : event.target.value.trim() !== '';
            if (!isValid) {
              setError(true)
              setErrorValue(true);
              setELabel(errorLabel ?? 'Compilare il campo richiesto')
            } else {
              setError(false)
              setErrorValue(false);
              setELabel('')
            }
          } else {
            setError(false)
            setErrorValue(false);
            setELabel('')
          }
        }}
        {...props}
      />
    </InputWrapper>
  )
}

export default ThemedTextInput;
